import React from 'react';
import PropTypes from 'prop-types';
import LayoutPlaceholderCard from '../../../../components/shared/LayoutPlaceholderCard';
import { getCardPlaceholderMeta } from '../../../../utils/getCardDisplayTitle';
import { getHospitalLayoutSkeletonVariant } from '../../../../utils/hospitalLayoutPlaceholderSkeleton';
import { getLayoutPreviewSharePanelProps } from '../../../../utils/layoutPreviewShareBadge';
import {
	arePreviewPropsEqual,
	DaysPreviewBody,
	DxPreviewBody,
	FloorsPreviewBody,
	InsurancePreviewBody,
	PreviewShell,
	resolveDynamicPreviewBody,
	SliderPreviewBody,
	UserListPreviewBody,
} from '../../../../components/shared/layoutPreview';

/**
 * Layout-mode preview renderer for the Admission page.
 *
 * See `components/shared/layoutPreview/` for the shared primitives. This
 * file maps each Admission-specific `card.component` value to the matching
 * shared body, or delegates to the dynamic resolver.
 *
 * `averageStayDays` is a shape that only exists on the Admission page
 * (a big centered "X.X Days" value inside a circle with a calendar icon),
 * so its silhouette is defined locally here rather than polluting the
 * shared bodies module.
 */

// Page-local silhouette matching `AverageLengthOfStayCard`: a centered
// "value + unit" inside a circular chip. Keeps the preview instantly
// recognisable without needing to mount the real component or its data
// pipeline.
const AverageStayPreviewBody = React.memo(() => (
	<PreviewShell>
		<div style={{
			width: '100%', padding: '16px 16px 12px',
			display: 'flex', flexDirection: 'column',
			alignItems: 'center', justifyContent: 'flex-start', gap: 8,
		}}>
			{/* Header */}
			<div style={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'center', width: '100%', marginBottom: 4 }}>
				<span style={{ fontWeight: 600, fontSize: 15, color: '#1a1a1a', lineHeight: 1.3 }}>Average Length of Stay</span>
				<span style={{ fontSize: 11, color: '#6b7280', fontWeight: 400 }}>For the selected date range</span>
			</div>
			{/* Circle — gradient border via nested divs (matches ::before/::after approach) */}
			<div style={{ position: 'relative', width: 160, height: 160, flexShrink: 0 }}>
				{/* Gradient ring */}
				<div style={{
					position: 'absolute', inset: 0, borderRadius: '50%',
					background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%)',
				}} />
				{/* White inner circle */}
				<div style={{
					position: 'absolute', inset: 4, borderRadius: '50%',
					background: '#ffffff',
					display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
				}}>
					<span style={{ fontWeight: 700, fontSize: 38, color: '#000000', lineHeight: 1, letterSpacing: '-0.02em' }}>5.2</span>
					<span style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>Days</span>
				</div>
				{/* Calendar icon at bottom */}
				<div style={{
					position: 'absolute', bottom: -14, left: '50%', transform: 'translateX(-50%)',
					width: 32, height: 32, backgroundColor: '#2563eb', borderRadius: 6,
					display: 'flex', alignItems: 'center', justifyContent: 'center',
					boxShadow: '0 2px 4px rgba(96,165,250,0.3)',
				}}>
					{/* Calendar icon — simple SVG */}
					<svg width="18" height="18" viewBox="0 0 24 24" fill="white">
						<path d="M19 3h-1V1h-2v2H8V1H6v2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
					</svg>
				</div>
			</div>
		</div>
	</PreviewShell>
));
AverageStayPreviewBody.displayName = 'AverageStayPreviewBody';

const PREVIEW_BY_COMPONENT = {
	averageStayDays: AverageStayPreviewBody,
	doctor: UserListPreviewBody,
	days: DaysPreviewBody,
	dx: DxPreviewBody,
	insurance: InsurancePreviewBody,
	floors: FloorsPreviewBody,
	hospital: SliderPreviewBody,
};

const AdmissionCardLayoutPreview = ({ card, dynamicCards, sharedUsersByTabId }) => {
	const { title: placeholderTitle } = getCardPlaceholderMeta(card, {
		dynamicCards,
	});

	let PreviewBody = null;
	let previewBodyProps = {};
	if (card?.component === 'dynamic') {
		PreviewBody = resolveDynamicPreviewBody(card?.data);
		previewBodyProps = { role: 'generic', useGenericLabels: true };
	} else if (card?.component) {
		PreviewBody = PREVIEW_BY_COMPONENT[card.component] || null;
		if (card.component === 'doctor') {
			previewBodyProps = { role: 'doctor' };
		}
	}

	return (
		<LayoutPlaceholderCard
			variant="card"
			title={placeholderTitle}
			width={card?.width}
			sharePanel={getLayoutPreviewSharePanelProps(card, { sharedUsersByTabId })}
			skeletonVariant={getHospitalLayoutSkeletonVariant(card)}
			body={PreviewBody ? <PreviewBody {...previewBodyProps} /> : null}
		/>
	);
};

AdmissionCardLayoutPreview.propTypes = {
	card: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		component: PropTypes.string,
		type: PropTypes.string,
		width: PropTypes.number,
		data: PropTypes.object,
	}).isRequired,
	dynamicCards: PropTypes.array,
	sharedUsersByTabId: PropTypes.object,
};

AdmissionCardLayoutPreview.defaultProps = {
	dynamicCards: [],
	sharedUsersByTabId: undefined,
};

export default React.memo(AdmissionCardLayoutPreview, arePreviewPropsEqual);
